import { useState } from 'react';

import { Button } from '@/components/ui/button';

import { invoiceActionAvailable } from '../lib/invoice-actions';
import { issueActionCopy } from '../lib/issue-action-copy';
import { InvoiceReviewDialog } from './invoice-review-dialog';
import { IssueInvoiceConfirmation } from './issue-invoice-confirmation';
import { RecordInvoiceDialog } from './record-invoice-dialog';

import type { InvoiceRow } from '../types';

export interface IssueFlowButtonProps {
  readonly invoice: InvoiceRow;
  readonly onIssued?: (() => void) | undefined;
  readonly compact?: boolean | undefined;
}

// The consolidated Issue verb: the review step is folded in as step 1 of the
// flow rather than being its own action. The trigger opens the review dialog;
// its primary action saves any pending edits and hands off to the issue
// confirmation. Cancelling at either step leaves the Draft untouched, so the
// review stays reopenable — the pre-generation validator remains the backstop
// that enforces a completed review at issuance.
//
// The review step only exists for documents we render: it edits the generated
// document's fields and previews our PDF. A payable is the supplier's document,
// so on the Purchase side "Record Invoice" opens the record step instead — the
// draft's current values next to the received PDF, attachable there, with no
// way to continue without it (the server enforces the same gate).
function FirstStepDialog({
  invoice,
  onIssued,
  open,
  onOpenChange,
  onContinue,
}: Readonly<{
  invoice: InvoiceRow;
  onIssued: (() => void) | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: () => void;
}>) {
  if (invoice.invoice_direction !== 'Purchase') {
    return (
      <InvoiceReviewDialog
        open={open}
        onOpenChange={onOpenChange}
        invoice={invoice}
        onReviewed={onIssued}
        onContinueToIssue={onContinue}
      />
    );
  }
  return (
    <RecordInvoiceDialog
      open={open}
      onOpenChange={onOpenChange}
      invoice={invoice}
      onContinue={() => {
        onOpenChange(false);
        onContinue();
      }}
    />
  );
}

export function IssueFlowButton({ invoice, onIssued, compact }: IssueFlowButtonProps) {
  const [stepOpen, setStepOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!invoiceActionAvailable(invoice, 'issue')) return null;
  const copy = issueActionCopy(invoice);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={compact === true ? 'h-7 gap-1 px-3 text-xs' : undefined}
        data-testid={`invoice-${invoice.id}-issue`}
        onClick={() => {
          setStepOpen(true);
        }}
      >
        {copy.trigger}
      </Button>
      <FirstStepDialog
        invoice={invoice}
        onIssued={onIssued}
        open={stepOpen}
        onOpenChange={setStepOpen}
        onContinue={() => {
          setConfirmOpen(true);
        }}
      />
      <IssueInvoiceConfirmation
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        invoice={invoice}
        onIssued={onIssued}
      />
    </>
  );
}
